import { useMemo } from "react";
import { AlertTriangle, X, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatIDR, PROJECT_STATUS } from "@/lib/constants";

/**
 * 149-cw-r116 (2026-08-29) — Budget Alert Dialog.
 * Menampilkan daftar project yang melebihi (over) atau mendekati (near ≥80%) batas
 * kontrak. Diakses dari klik banner "Over Budget" / "Near Budget Limit" di
 * ProjectsPage. Klik row → navigate ke Summary Project + set filter (opsional
 * kalau caller sediakan `onProjectClick`).
 *
 * Props:
 * - open, onOpenChange: dialog control
 * - kind: "over" | "near"
 * - projects: array of all projects (dari state ProjectsPage)
 * - onProjectClick: fn(project) → callback saat user klik salah satu baris (opsional)
 */
export default function BudgetAlertDialog({ open, onOpenChange, kind, projects, onProjectClick }) {
  const filtered = useMemo(() => {
    if (!kind || !Array.isArray(projects)) return [];
    return projects
      .filter((p) => {
        if (p.project_type === "internal") return false;
        const cv = Number(p.contract_value) || 0;
        const exp = Number(p.total_expense) || 0;
        if (cv <= 0) return false;
        if (kind === "over") return exp > cv;
        // "near": 80% ≤ pct ≤ 100% (exclude yang sudah over — mereka di kartu Over)
        const pct = (exp / cv) * 100;
        return pct >= 80 && exp <= cv;
      })
      .map((p) => {
        const cv = Number(p.contract_value) || 0;
        const exp = Number(p.total_expense) || 0;
        const pct = cv > 0 ? (exp / cv) * 100 : 0;
        return { ...p, __budget_pct: pct, __budget_over_amount: Math.max(0, exp - cv) };
      })
      .sort((a, b) => b.__budget_pct - a.__budget_pct);
  }, [kind, projects]);

  const isOver = kind === "over";
  const title = isOver ? "Daftar Project Over Budget" : "Daftar Project Mendekati Batas Kontrak";
  const subtitle = isOver
    ? "Project di bawah ini pengeluaran sudah MELEBIHI nilai kontrak. Segera evaluasi biaya."
    : "Project di bawah ini pengeluaran sudah ≥80% dari nilai kontrak. Waspada agar tidak melewati batas.";
  const tone = isOver
    ? { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-700", accent: "text-rose-800", badge: "bg-rose-100 text-rose-800 border-rose-300" }
    : { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", accent: "text-amber-800", badge: "bg-amber-100 text-amber-800 border-amber-300" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none" data-testid="budget-alert-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <AlertTriangle size={18} className={isOver ? "text-rose-600" : "text-amber-600"} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className={`${tone.bg} ${tone.border} border p-3 flex items-center justify-between`} data-testid="budget-alert-summary">
          <div className="text-xs">
            <div className={`overline ${tone.text}`}>Jumlah Project</div>
            <div className={`font-display text-2xl tabular-nums font-bold ${tone.accent}`}>
              {filtered.length}
            </div>
          </div>
          <div className="text-xs text-right">
            <div className={`overline ${tone.text}`}>{isOver ? "Total Selisih (Rp)" : "Total Terpakai (Rp)"}</div>
            <div className={`font-display text-lg tabular-nums font-bold ${tone.accent}`}>
              {formatIDR(filtered.reduce((s, p) => s + (isOver ? p.__budget_over_amount : Number(p.total_expense) || 0), 0))}
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-8 text-xs italic text-slate-400">
            Tidak ada project sesuai kriteria.
          </div>
        ) : (
          <div className="border border-slate-200 max-h-[50vh] overflow-y-auto" data-testid="budget-alert-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b-2 border-slate-300 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 overline text-slate-700">Project / WO</th>
                  <th className="px-3 py-2 overline text-slate-700">Client</th>
                  <th className="px-3 py-2 overline text-slate-700 text-right">Nilai Kontrak</th>
                  <th className="px-3 py-2 overline text-slate-700 text-right">Pengeluaran</th>
                  <th className="px-3 py-2 overline text-slate-700 text-right">% Terpakai</th>
                  <th className="px-3 py-2 overline text-slate-700 text-right">{isOver ? "Selisih" : "Sisa"}</th>
                  <th className="px-3 py-2 overline text-slate-700"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const cv = Number(p.contract_value) || 0;
                  const exp = Number(p.total_expense) || 0;
                  const pct = p.__budget_pct;
                  const isChild = p.project_type === "working_order" || p.project_type === "variation_order";
                  const label = p.project_type === "working_order"
                    ? `${p.wo_number || "-"} · ${p.project_name || "-"}`
                    : p.project_type === "variation_order"
                    ? `VO · ${p.variation_order_name || p.project_name || "-"}`
                    : (p.project_name || "-");
                  return (
                    <tr
                      key={p.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${onProjectClick ? "cursor-pointer" : ""}`}
                      onClick={() => onProjectClick?.(p)}
                      data-testid={`budget-alert-row-${p.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-semibold text-[#0B1D3A]">{label}</div>
                        {isChild && p.parent_project_name && (
                          <div className="text-[10px] text-slate-500">↳ {p.parent_project_name}</div>
                        )}
                        {p.status && p.status !== "active" && (
                          <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border border-slate-300 text-slate-600 bg-slate-50">
                            {PROJECT_STATUS?.[p.status] || p.status}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{p.client || "-"}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-slate-700">{formatIDR(cv)}</td>
                      <td className="px-3 py-2 tabular-nums text-right font-semibold text-slate-800">{formatIDR(exp)}</td>
                      <td className={`px-3 py-2 tabular-nums text-right font-bold ${tone.accent}`}>
                        {pct.toFixed(1)}%
                      </td>
                      <td className={`px-3 py-2 tabular-nums text-right font-semibold ${isOver ? "text-rose-700" : "text-amber-700"}`}>
                        {isOver ? `− ${formatIDR(p.__budget_over_amount)}` : formatIDR(Math.max(0, cv - exp))}
                      </td>
                      <td className="px-3 py-2">
                        {onProjectClick && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F]">
                            <ArrowRight size={11} />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
